import {
  createContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
} from "react";
import api, { GetRoomResponse } from "../../../hooks/api";
import { useAxios } from "../../../hooks/axios";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";

interface IRoomContext {
  room: GetRoomResponse["room"] | undefined;
  setRoom: Dispatch<SetStateAction<GetRoomResponse["room"] | undefined>>;
  organization: GetRoomResponse["organization"] | undefined;
  setOrganization: Dispatch<
    SetStateAction<GetRoomResponse["organization"] | undefined>
  >;
  glossaries: GetRoomResponse["glossaries"] | undefined;
  setGlossaries: Dispatch<
    SetStateAction<GetRoomResponse["glossaries"] | undefined>
  >;
  loading: boolean;
  error: unknown | undefined;
}

export const RoomContext = createContext<IRoomContext>({} as IRoomContext);

export const RoomProvider = (props: { children: ReactNode | ReactNode[] }) => {
  const params = useParams();
  const roomId = useMemo(() => {
    return params.id!;
  }, [params.id]);

  const [room, setRoom] = useState<GetRoomResponse["room"]>();
  const [organization, setOrganization] =
    useState<GetRoomResponse["organization"]>();
  const [glossaries, setGlossaries] = useState<GetRoomResponse["glossaries"]>();

  const axios = useAxios();
  const getRoom = api.useGetRoom(axios);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>();
  const location = useLocation();

  useEffect(() => {
    (async () => {
      setError(undefined);
      setLoading(true);
      try {
        const response = await getRoom({ id: roomId });
        setRoom(response.data.room);
        setOrganization(response.data.organization);
        setGlossaries(response.data.glossaries);
      } catch (error) {
        console.error(error);
        setError(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [getRoom, roomId, location]);

  return (
    <RoomContext.Provider
      value={{
        room,
        setRoom,
        organization,
        setOrganization,
        glossaries,
        setGlossaries,
        loading,
        error,
      }}
    >
      {props.children}
    </RoomContext.Provider>
  );
};
